// Client for the lietome.ch per-person analysis service.
//
// The real service does not exist yet (see plan Phase 2 — it must be built on
// the VPS and served same-origin at /api/analyze). Until then, and in dev, a
// local mock returns a plausible payload so the whole pipeline is testable.
//
// IMPORTANT: `cyclePhase` is NON-SCIENTIFIC. Menstrual-cycle phase cannot be
// inferred from a face image at any distance. It is returned only as an
// explicitly-disclaimed field and must never be presented as fact.

import { CONFIG } from "../config";
import type { BBox, PersonAnalysis, TrackedFace } from "../types";

// Mock analyser is on in dev, or when built with VITE_USE_MOCK=1 (shareable
// test builds with no backend — nothing leaves the device).
const USE_MOCK = import.meta.env.DEV || import.meta.env.VITE_USE_MOCK === "1";

const CYCLE_DISCLAIMER =
  "Not scientifically detectable from a camera — shown for the app's stated purpose only, not a real medical estimate.";

/** Crop a padded face region from a frame source (e.g. the <video>) to a JPEG blob. */
export async function cropBBoxToBlob(
  source: CanvasImageSource,
  sw: number,
  sh: number,
  bbox: BBox,
): Promise<Blob | null> {
  const pad = CONFIG.analysis.cropPadding;

  const x = Math.max(0, (bbox.x - bbox.w * pad) * sw);
  const y = Math.max(0, (bbox.y - bbox.h * pad) * sh);
  const w = Math.min(sw - x, bbox.w * (1 + 2 * pad) * sw);
  const h = Math.min(sh - y, bbox.h * (1 + 2 * pad) * sh);
  if (w < 8 || h < 8) return null;

  const out = document.createElement("canvas");
  out.width = Math.round(w);
  out.height = Math.round(h);
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, x, y, w, h, 0, 0, out.width, out.height);

  return new Promise((resolve) =>
    out.toBlob((b) => resolve(b), "image/jpeg", CONFIG.analysis.cropJpegQuality),
  );
}

/** POST a cropped face to the analyser and parse the response. */
export async function requestAnalysis(
  blob: Blob,
  meta: { trackId: number; distanceM: number },
): Promise<PersonAnalysis> {
  if (USE_MOCK) return mockAnalysis(meta);

  const form = new FormData();
  form.append("image", blob, "face.jpg");
  form.append("meta", JSON.stringify(meta));

  const res = await fetch(CONFIG.analysis.endpoint, { method: "POST", body: form });
  if (!res.ok) throw new Error(`analyze ${res.status}`);
  const raw = (await res.json()) as Partial<PersonAnalysis>;

  return {
    sex: raw.sex ?? null,
    ageBand: raw.ageBand ?? null,
    expression: raw.expression ?? null,
    attentionScore: raw.attentionScore ?? null,
    distanceM: raw.distanceM ?? meta.distanceM,
    cyclePhase: raw.cyclePhase
      ? { ...raw.cyclePhase, disclaimer: raw.cyclePhase.disclaimer || CYCLE_DISCLAIMER }
      : { value: null, confidence: 0, disclaimer: CYCLE_DISCLAIMER },
    receivedAt: Date.now(),
  };
}

function mockAnalysis(meta: { trackId: number; distanceM: number }): Promise<PersonAnalysis> {
  const sexes = ["male", "female"];
  const ages = ["teens", "20s", "30s", "40s", "50s+"];
  const moods = ["neutral", "focused", "smiling", "surprised", "tense"];
  const pick = <T>(arr: T[], salt: number): T => arr[(meta.trackId + salt) % arr.length]!;

  const data: PersonAnalysis = {
    sex: pick(sexes, 0),
    ageBand: pick(ages, 2),
    expression: pick(moods, 1),
    attentionScore: 0.6 + ((meta.trackId * 7) % 40) / 100,
    distanceM: meta.distanceM,
    cyclePhase: { value: null, confidence: 0, disclaimer: CYCLE_DISCLAIMER },
    receivedAt: Date.now(),
  };
  // Simulate network latency so the "pending" UI state is exercised.
  return new Promise((resolve) => setTimeout(() => resolve(data), 450));
}

/**
 * Decide whether a tracked face is due for analysis and, if so, run it and
 * store the result on the track. Dedupes: never fires while one is pending, and
 * respects the minimum-locked and re-analyse-interval gates.
 */
export async function maybeAnalyse(
  track: TrackedFace,
  getCrop: (bbox: BBox) => Promise<Blob | null>,
  now: number,
): Promise<void> {
  if (!track.locked) return;
  if (track.analysis.status === "pending") return;

  const lockedFor = track.lockedSince ? now - track.lockedSince : 0;
  if (lockedFor < CONFIG.analysis.minLockedMsBeforeAnalyse) return;

  const sinceLast = now - track.lastAnalysedAt;
  const alreadyHas = track.analysis.status === "done";
  if (alreadyHas && sinceLast < CONFIG.analysis.reanalyseIntervalMs) return;

  track.analysis = { status: "pending" };
  track.lastAnalysedAt = now;

  try {
    const blob = await getCrop(track.bbox);
    if (!blob) {
      track.analysis = { status: "error", message: "crop failed" };
      return;
    }
    const data = await requestAnalysis(blob, { trackId: track.id, distanceM: track.distanceM });
    track.analysis = { status: "done", data };
  } catch (err) {
    track.analysis = { status: "error", message: err instanceof Error ? err.message : "failed" };
  }
}
